import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createVNode as _createVNode, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "absolute left-3 top-3 z-30 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
}
const _hoisted_2 = { class: "flex cursor-pointer items-center" }
const _hoisted_3 = ["value", "checked"]
const _hoisted_4 = { class: "absolute right-3 top-3 z-20 flex flex-col items-end gap-2" }
const _hoisted_5 = {
  key: 0,
  class: "flex items-center space-x-1 rounded-full bg-gradient-to-r from-red-500 to-pink-500 px-3 py-1 text-xs font-medium text-white shadow-lg backdrop-blur-sm"
}
const _hoisted_6 = {
  key: 1,
  class: "flex items-center space-x-1 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 px-3 py-1 text-xs font-medium text-white shadow-lg backdrop-blur-sm"
}
const _hoisted_7 = { class: "rounded-full bg-white/25 px-2 py-0.5 text-[11px] font-bold" }
const _hoisted_8 = {
  key: 0,
  class: "absolute inset-0 z-10 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
}
const _hoisted_9 = ["src"]
const _hoisted_10 = { class: "absolute inset-0 z-20 flex items-center justify-center opacity-0 transition-all duration-300 group-hover:opacity-100" }
const _hoisted_11 = { class: "flex translate-y-4 space-x-3 transition-transform duration-300 group-hover:translate-y-0" }
const _hoisted_12 = { class: "absolute bottom-3 right-3 rounded-full bg-black/70 px-2.5 py-1 text-xs text-white backdrop-blur-sm shadow-lg flex items-center space-x-1" }
const _hoisted_13 = { class: "font-medium" }
const _hoisted_14 = { class: "p-3" }
const _hoisted_15 = { class: "mb-2 flex items-start justify-between gap-3" }
const _hoisted_16 = { class: "min-w-0 flex-1" }
const _hoisted_17 = { class: "truncate text-sm font-semibold text-gray-900 dark:text-white" }
const _hoisted_18 = { class: "mt-1 flex items-center justify-between gap-2" }
const _hoisted_19 = { class: "truncate rounded-full bg-purple-100 px-2 py-1 text-xs font-medium text-purple-800 dark:bg-purple-900/30 dark:text-purple-200" }
const _hoisted_20 = { class: "shrink-0 text-xs text-gray-500 dark:text-gray-400" }
const _hoisted_21 = { class: "grid grid-cols-2 gap-1.5 text-xs" }
const _hoisted_22 = { class: "flex items-center group/ratio relative text-gray-600 dark:text-gray-400" }
const _hoisted_23 = { class: "flex items-center justify-end gap-1.5" }
const _hoisted_24 = { class: "rounded-full bg-white p-1 border border-gray-200 shadow-sm dark:border-gray-600 dark:bg-gray-700" }
const _hoisted_25 = { class: "text-gray-600 dark:text-gray-400" }
const _hoisted_26 = {
  key: 0,
  class: "mt-2 rounded-xl bg-gray-50 p-2 text-xs leading-4 text-gray-600 dark:bg-gray-900/60 dark:text-gray-300"
}

import { computed, ref } from 'vue'
import SimpleSecureImage from '../ui/SimpleSecureImage.vue'
import type { Wallpaper } from '../../firebase/schema'
import { cloudinaryService } from '../../services/cloudinaryService'
import { isLikelyVideoUrl } from '../../utils/validation'
import type { DeviceWallpaperTab } from '../../composables/useDeviceWallpaperTabs'

interface Props {
  wallpaper: Wallpaper
  categoryMap: Record<string, string>
  showAdminActions?: boolean
  isSelected?: boolean
  device: DeviceWallpaperTab
  deviceLabel: string
  deviceShortLabel: string
  deviceEmoji: string
  subtitle: string
  aspectRatioClass: string
  aspectRatioLabel: string
  targetAspect: number
  targetWidth: number
  targetHeight: number
  badgeClass: string
}


export default /*@__PURE__*/_defineComponent({
  __name: 'BaseDeviceWallpaperCard',
  props: {
    wallpaper: {},
    categoryMap: {},
    showAdminActions: { type: Boolean, default: false },
    isSelected: { type: Boolean, default: false },
    device: {},
    deviceLabel: {},
    deviceShortLabel: {},
    deviceEmoji: {},
    subtitle: {},
    aspectRatioClass: {},
    aspectRatioLabel: {},
    targetAspect: {},
    targetWidth: {},
    targetHeight: {},
    badgeClass: {}
  },
  emits: ["toggle-selection", "view", "download", "favorite", "edit", "delete"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

const wallpaper = computed(() => props.wallpaper)
const isHovering = ref(false)

const getCategoryName = (categoryId?: string) => {
  if (!categoryId) return 'Uncategorized'
  return props.categoryMap[categoryId] || categoryId
}

const formatDate = (date: Date | string) => {
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

const getDeviceCount = () => {
  const wp = wallpaper.value
  if (wp.isWallpaperPack && wp.deviceVariations) {
    return Object.keys(wp.deviceVariations).length
  }
  return wp.supportedDevices?.length || (wp.primaryDevice ? 1 : 0)
}

const isLiveWallpaper = computed(() => {
  const wp = wallpaper.value
  const packLiveUrl = getSelectedPackLiveUrl()
  return Boolean(
    wp.isVideo ||
    wp.isAnimated ||
    (wp.videoUrl && wp.videoUrl.trim().length > 0) ||
    isLikelyVideoUrl(wp.imageUrl) ||
    Boolean(packLiveUrl)
  )
})

const getSelectedPackVariation = () => {
  const wp = wallpaper.value

  if (!wp.isWallpaperPack) return null

  if (wp.deviceVariationsList && wp.deviceVariationsList.length > 0) {
    const deviceGroup = wp.deviceVariationsList.find(group => group.device === props.device)
    if (deviceGroup && deviceGroup.variations.length > 0) {
      return deviceGroup.variations.find(v => v.isPrimary) || deviceGroup.variations[0]
    }
  }

  const deviceVariation = wp.deviceVariations?.[props.device]
  if (deviceVariation) {
    return {
      imageUrl: deviceVariation.imageUrl,
      thumbnailUrl: deviceVariation.thumbnailUrl,
      isPrimary: true
    }
  }

  return null
}

const getSelectedPackLiveUrl = () => {
  const variation = getSelectedPackVariation()
  if (!variation?.imageUrl?.trim()) return ''
  return isLikelyVideoUrl(variation.imageUrl) ? variation.imageUrl : ''
}

const getStaticPreviewImageUrl = () => {
  const wp = wallpaper.value

  if (wp.isWallpaperPack) {
    const variation = getSelectedPackVariation()
    if (variation?.imageUrl?.trim()) {
      if (isLikelyVideoUrl(variation.imageUrl)) {
        if (variation.thumbnailUrl?.trim()) {
          return variation.thumbnailUrl
        }

        try {
          return cloudinaryService.generateThumbnailUrl(variation.imageUrl, props.targetWidth, props.targetHeight)
        } catch (error) {
          console.warn('[BaseDeviceWallpaperCard] Failed to generate poster frame for pack live variation:', error)
        }

        return ''
      }

      return variation.imageUrl || variation.thumbnailUrl || ''
    }

    if (variation?.thumbnailUrl?.trim()) {
      return variation.thumbnailUrl
    }
  }

  if (wp.isVideo || wp.isAnimated || wp.videoUrl || isLikelyVideoUrl(wp.imageUrl)) {
    if (wp.videoThumbnail?.trim()) {
      return wp.videoThumbnail
    }

    if (wp.thumbnailUrl?.trim()) {
      return wp.thumbnailUrl
    }

    if (wp.videoUrl?.trim()) {
      try {
        return cloudinaryService.generateThumbnailUrl(wp.videoUrl, props.targetWidth, props.targetHeight)
      } catch (error) {
        console.warn('[BaseDeviceWallpaperCard] Failed to generate poster frame for live wallpaper:', error)
      }
    }

    if (wp.imageUrl?.trim() && isLikelyVideoUrl(wp.imageUrl)) {
      try {
        return cloudinaryService.generateThumbnailUrl(wp.imageUrl, props.targetWidth, props.targetHeight)
      } catch (error) {
        console.warn('[BaseDeviceWallpaperCard] Failed to generate poster frame from imageUrl video:', error)
      }
    }

    return ''
  }

  return wp.imageUrl || wp.thumbnailUrl || ''
}

const staticPreviewUrl = computed(() => getStaticPreviewImageUrl())

const livePreviewUrl = computed(() => {
  const wp = wallpaper.value

  if (wp.videoUrl?.trim()) {
    return wp.videoUrl
  }

  const packLiveUrl = getSelectedPackLiveUrl()
  if (packLiveUrl) {
    return packLiveUrl
  }

  if (isLikelyVideoUrl(wp.imageUrl)) {
    return wp.imageUrl
  }

  return ''
})

const onLivePreviewLoad = () => {
  // Live preview is intentionally silent; the static preview remains visible until hover.
}

const onLivePreviewError = (event: Event) => {
  console.warn('[BaseDeviceWallpaperCard] Live preview failed to load:', event)
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: "group relative overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-purple-300 hover:shadow-2xl dark:border-gray-700 dark:bg-gray-800 dark:hover:border-purple-500",
    onMouseenter: _cache[5] || (_cache[5] = ($event: any) => (isHovering.value = true)),
    onMouseleave: _cache[6] || (_cache[6] = ($event: any) => (isHovering.value = false))
  }, [
    (_ctx.showAdminActions)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _createElementVNode("label", _hoisted_2, [
            _createElementVNode("input", {
              type: "checkbox",
              value: wallpaper.value.id,
              checked: _ctx.isSelected,
              onChange: _cache[0] || (_cache[0] = ($event: any) => (emit('toggle-selection', wallpaper.value.id))),
              class: "h-5 w-5 rounded border-gray-300 bg-white text-purple-600 shadow-lg transition-all duration-200 hover:scale-110 focus:ring-purple-500 dark:border-gray-500 dark:bg-gray-700"
            }, null, 40, _hoisted_3)
          ])
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_4, [
      (isLiveWallpaper.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_5, _cache[7] || (_cache[7] = [
            _createElementVNode("span", { class: "relative mr-1 inline-flex h-2 w-2" }, [
              _createElementVNode("span", { class: "absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75" }),
              _createElementVNode("span", { class: "relative inline-flex h-2 w-2 rounded-full bg-white" })
            ], -1),
            _createElementVNode("span", null, "LIVE", -1)
          ])))
        : _createCommentVNode("", true),
      (wallpaper.value.isWallpaperPack)
        ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
            _cache[8] || (_cache[8] = _createElementVNode("span", { class: "font-medium" }, "PACK", -1)),
            _createElementVNode("span", _hoisted_7, _toDisplayString(getDeviceCount()), 1)
          ]))
        : (_openBlock(), _createElementBlock("div", {
            key: 2,
            class: _normalizeClass(["flex items-center space-x-1 rounded-full px-3 py-1 text-xs font-medium text-white shadow-lg backdrop-blur-sm", _ctx.badgeClass])
          }, [
            _createElementVNode("span", null, _toDisplayString(_ctx.deviceEmoji), 1),
            _createElementVNode("span", null, _toDisplayString(_ctx.deviceShortLabel), 1)
          ], 2))
    ]),
    _createElementVNode("div", {
      class: _normalizeClass([
        'relative overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800',
        _ctx.aspectRatioClass
      ])
    }, [
      _createVNode(SimpleSecureImage, {
        "image-url": staticPreviewUrl.value,
        "wallpaper-id": wallpaper.value.id,
        "is-preview": true,
        alt: wallpaper.value.title || `${_ctx.deviceLabel} wallpaper`,
        "container-class": 'absolute inset-0 h-full w-full',
        "image-class": 'h-full w-full object-cover transition-opacity duration-300',
        "target-aspect": _ctx.targetAspect,
        "target-width": _ctx.targetWidth,
        "target-height": _ctx.targetHeight,
        "crop-mode": "fill"
      }, null, 8, ["image-url", "wallpaper-id", "alt", "target-aspect", "target-width", "target-height"]),
      (isLiveWallpaper.value && livePreviewUrl.value && isHovering.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
            _createElementVNode("video", {
              src: livePreviewUrl.value,
              class: "h-full w-full object-cover",
              autoplay: "",
              muted: "",
              loop: "",
              playsinline: "",
              preload: "metadata",
              onLoadeddata: onLivePreviewLoad,
              onError: onLivePreviewError
            }, null, 40, _hoisted_9)
          ]))
        : _createCommentVNode("", true),
      _cache[14] || (_cache[14] = _createElementVNode("div", { class: "absolute inset-0 bg-gradient-to-t from-black/35 via-transparent to-transparent opacity-0 transition-all duration-300 group-hover:opacity-100" }, null, -1)),
      _createElementVNode("div", _hoisted_10, [
        _createElementVNode("div", _hoisted_11, [
          _createElementVNode("button", {
            onClick: _cache[1] || (_cache[1] = _withModifiers(($event: any) => (emit('view', wallpaper.value)), ["stop"])),
            class: "rounded-full bg-white/95 p-3 shadow-xl backdrop-blur-sm transition-all duration-200 hover:scale-110 hover:bg-white dark:bg-gray-800/95 dark:hover:bg-gray-700",
            title: "View wallpaper"
          }, _cache[9] || (_cache[9] = [
            _createElementVNode("svg", {
              class: "h-5 w-5 text-purple-600 dark:text-purple-400",
              fill: "none",
              stroke: "currentColor",
              viewBox: "0 0 24 24"
            }, [
              _createElementVNode("path", {
                "stroke-linecap": "round",
                "stroke-linejoin": "round",
                "stroke-width": "2",
                d: "M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              }),
              _createElementVNode("path", {
                "stroke-linecap": "round",
                "stroke-linejoin": "round",
                "stroke-width": "2",
                d: "M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
              })
            ], -1)
          ])),
          _createElementVNode("button", {
            onClick: _cache[2] || (_cache[2] = _withModifiers(($event: any) => (emit('favorite', wallpaper.value)), ["stop"])),
            class: "rounded-full bg-white/95 p-3 shadow-xl backdrop-blur-sm transition-all duration-200 hover:scale-110 hover:bg-white dark:bg-gray-800/95 dark:hover:bg-gray-700",
            title: "Add to favorites"
          }, _cache[10] || (_cache[10] = [
            _createElementVNode("svg", {
              class: "h-5 w-5 text-red-500 dark:text-red-400",
              fill: "none",
              stroke: "currentColor",
              viewBox: "0 0 24 24"
            }, [
              _createElementVNode("path", {
                "stroke-linecap": "round",
                "stroke-linejoin": "round",
                "stroke-width": "2",
                d: "M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
              })
            ], -1)
          ])),
          (_ctx.showAdminActions)
            ? (_openBlock(), _createElementBlock("button", {
                key: 0,
                onClick: _cache[3] || (_cache[3] = _withModifiers(($event: any) => (emit('edit', wallpaper.value)), ["stop"])),
                class: "rounded-full bg-white/95 p-3 shadow-xl backdrop-blur-sm transition-all duration-200 hover:scale-110 hover:bg-white dark:bg-gray-800/95 dark:hover:bg-gray-700",
                title: "Edit wallpaper"
              }, _cache[11] || (_cache[11] = [
                _createElementVNode("svg", {
                  class: "h-5 w-5 text-orange-600 dark:text-orange-400",
                  fill: "none",
                  stroke: "currentColor",
                  viewBox: "0 0 24 24"
                }, [
                  _createElementVNode("path", {
                    "stroke-linecap": "round",
                    "stroke-linejoin": "round",
                    "stroke-width": "2",
                    d: "M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                  })
                ], -1)
              ])))
            : _createCommentVNode("", true),
          (_ctx.showAdminActions)
            ? (_openBlock(), _createElementBlock("button", {
                key: 1,
                onClick: _cache[4] || (_cache[4] = _withModifiers(($event: any) => (emit('delete', wallpaper.value)), ["stop"])),
                class: "rounded-full bg-white/95 p-3 shadow-xl backdrop-blur-sm transition-all duration-200 hover:scale-110 hover:bg-white dark:bg-gray-800/95 dark:hover:bg-gray-700",
                title: "Delete wallpaper"
              }, _cache[12] || (_cache[12] = [
                _createElementVNode("svg", {
                  class: "h-5 w-5 text-red-600 dark:text-red-400",
                  fill: "none",
                  stroke: "currentColor",
                  viewBox: "0 0 24 24"
                }, [
                  _createElementVNode("path", {
                    "stroke-linecap": "round",
                    "stroke-linejoin": "round",
                    "stroke-width": "2",
                    d: "M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  })
                ], -1)
              ])))
            : _createCommentVNode("", true)
        ])
      ]),
      _createElementVNode("div", _hoisted_12, [
        _cache[13] || (_cache[13] = _createElementVNode("svg", {
          class: "h-3 w-3",
          fill: "none",
          stroke: "currentColor",
          viewBox: "0 0 24 24"
        }, [
          _createElementVNode("path", {
            "stroke-linecap": "round",
            "stroke-linejoin": "round",
            "stroke-width": "2",
            d: "M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          })
        ], -1)),
        _createElementVNode("span", _hoisted_13, _toDisplayString(wallpaper.value.downloadCount || 0), 1)
      ])
    ], 2),
    _createElementVNode("div", _hoisted_14, [
      _createElementVNode("div", _hoisted_15, [
        _createElementVNode("div", _hoisted_16, [
          _createElementVNode("h3", _hoisted_17, _toDisplayString(wallpaper.value.title || `${_ctx.deviceLabel} wallpaper`), 1),
          _createElementVNode("div", _hoisted_18, [
            _createElementVNode("span", _hoisted_19, _toDisplayString(getCategoryName(wallpaper.value.category)), 1),
            _createElementVNode("span", _hoisted_20, _toDisplayString(formatDate(wallpaper.value.createdAt)), 1)
          ])
        ])
      ]),
      _createElementVNode("div", _hoisted_21, [
        _createElementVNode("div", _hoisted_22, [
          _cache[15] || (_cache[15] = _createElementVNode("svg", {
            class: "mr-1 h-3 w-3 text-gray-400",
            fill: "none",
            stroke: "currentColor",
            viewBox: "0 0 24 24"
          }, [
            _createElementVNode("path", {
              "stroke-linecap": "round",
              "stroke-linejoin": "round",
              "stroke-width": "2",
              d: "M4 6h16M4 12h16M4 18h16"
            })
          ], -1)),
          _createElementVNode("span", null, _toDisplayString(_ctx.aspectRatioLabel), 1)
        ]),
        _createElementVNode("div", _hoisted_23, [
          _createElementVNode("span", _hoisted_24, _toDisplayString(_ctx.deviceEmoji), 1),
          _createElementVNode("span", _hoisted_25, _toDisplayString(_ctx.deviceLabel), 1)
        ])
      ]),
      (_ctx.subtitle?.trim())
        ? (_openBlock(), _createElementBlock("div", _hoisted_26, _toDisplayString(_ctx.subtitle), 1))
        : _createCommentVNode("", true)
    ])
  ], 32))
}
}

})